import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { brand } from '@/lib/brand';
import { useAuthMe, useLogin } from '@/hooks/useAuth';
import { PlaceholderBanner } from '@/components/PlaceholderBanner';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading } = useAuthMe();
  const login = useLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const from =
    (location.state as { from?: string } | null)?.from &&
    (location.state as { from?: string }).from !== '/login'
      ? (location.state as { from: string }).from
      : '/';

  if (!isLoading && data?.user) {
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await login.mutateAsync({ email: email.trim(), password });
      navigate(from, { replace: true });
    } catch {
      // error shown via login.error
    }
  }

  return (
    <div className="relative min-h-dvh">
      <PlaceholderBanner />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(61,107,82,0.25),transparent_45%),radial-gradient(circle_at_80%_0%,rgba(184,137,61,0.22),transparent_40%),linear-gradient(160deg,#1b2a24,#121816)]" />
      <div className="relative mx-auto flex min-h-dvh max-w-md flex-col justify-center px-4 py-10">
        <div className="animate-fade-up rounded-2xl border border-white/10 bg-white/95 p-6 shadow-xl shadow-black/20">
          <p className="font-display text-2xl font-semibold text-dome-forest">
            {brand.productFull}
          </p>
          <p className="mt-1 text-sm text-[var(--muted)]">
            Staff sign-in for {brand.org}. Full access once authenticated.
          </p>
          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Email</span>
              <input
                className="w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Password</span>
              <input
                className="w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            {login.isError ? (
              <p className="text-sm text-dome-danger" role="alert">
                {(login.error as Error).message || 'Invalid email or password'}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={login.isPending}
              className="w-full rounded-lg bg-dome-forest px-4 py-2.5 font-medium text-dome-sand disabled:opacity-60"
            >
              {login.isPending ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
          <p className="mt-4 text-xs text-[var(--muted)]">
            Auth is cookie-based staff sessions via{' '}
            <code className="font-mono">/api/auth/*</code> — not{' '}
            <code className="font-mono">supabase.auth</code>.
          </p>
        </div>
      </div>
    </div>
  );
}
